import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { hasPermission } from "../utils/auth";

export default function UploadContractPage({ user }) {
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [durationMonths, setDurationMonths] = useState("");
  const [isOpenEnded, setIsOpenEnded] = useState(false);
  const [grossAmount, setGrossAmount] = useState("");
  const [netAmount, setNetAmount] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [processingStatus, setProcessingStatus] = useState("");
  const [handwritingMode, setHandwritingMode] = useState(false);
  const [overlayProcessing, setOverlayProcessing] = useState(false);
  const [previewTypeName, setPreviewTypeName] = useState("");

  const applyDateFields = (meta = {}) => {
    const end = meta.end_date || "";
    const start = meta.start_date || "";
    const duration =
      meta.duration_months != null ? String(meta.duration_months) : "";
    const open =
      meta.is_open_ended === true || meta.is_open_ended === 1 || !end;

    setStartDate(start);
    setEndDate(end);
    setIsOpenEnded(open);
    setDurationMonths(open ? "" : duration);
  };

  const fetchTypes = async () => {
    try {
      const res = await api.get("/contract-types");
      setTypes(res.data);
    } catch (err) {
      console.error("Error fetching contract types:", err);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  const handleFileChange = (e) => {
    const picked = e.target.files[0] || null;
    setFile(picked);
    if (picked) {
      previewMetadata(picked);
    }
  };

  const previewMetadata = async (pickedFile) => {
    setOverlayProcessing(true);
    setProcessingStatus("Extracting fields from PDF…");
    try {
      const fd = new FormData();
      fd.append("file", pickedFile);
      const res = await api.post(
        `/contracts/preview-metadata?handwriting=${handwritingMode ? "1" : "0"}`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      const meta = res.data?.metadata || {};
      if (meta.title) {
        setTitle(meta.title);
      } else if (pickedFile?.name) {
        setTitle(pickedFile.name.replace(/\.pdf$/i, ""));
      }
      if (meta.short_description) {
        setDescription(meta.short_description);
      }
      if (meta.contract_type) {
        const match = types.find(
          (t) => t.name.toLowerCase() === meta.contract_type.toLowerCase()
        );
        if (match) {
          setSelectedTypeId(String(match.id));
          setPreviewTypeName("");
      } else {
        setPreviewTypeName(meta.contract_type);
      }
    }
    applyDateFields(meta);
    setGrossAmount(
      meta.gross_amount != null ? String(meta.gross_amount) : ""
    );
      setNetAmount(meta.net_amount != null ? String(meta.net_amount) : "");
      setCurrency(meta.currency || currency || "EUR");
      setProcessingStatus("Fields extracted. You can edit before upload.");
    } catch (err) {
      console.error("Preview metadata error:", err);
      setProcessingStatus("Could not extract fields automatically. Please enter manually.");
    } finally {
      setOverlayProcessing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setProcessingStatus("");
    if (!file) {
      setMessage("Please select a PDF file.");
      return;
    }
    if (!title.trim()) {
      setMessage("Title is required.");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
      formData.append("contract_type_id", selectedTypeId || "");

      formData.append("start_date", startDate || "");
      formData.append("end_date", endDate || "");
      formData.append("duration_months", durationMonths || "");
      formData.append("is_open_ended", isOpenEnded ? "1" : "0");
      formData.append("gross_amount", grossAmount || "");
      formData.append("net_amount", netAmount || "");
      formData.append("currency", currency || "EUR");
      formData.append("file", file);

      const res = await api.post("/contracts", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const newId = res.data.id;
      setMessage(`Contract uploaded (ID: ${newId}). Auto-processing started...`);
      setProcessingStatus("Processing...");
      setOverlayProcessing(true);
      navigate("/contracts");
      return;

      // Poll status until READY or FAILED and hydrate fields from extracted metadata
      const poll = async (attempt = 0) => {
        try {
          const detail = await api.get(`/contracts/${newId}`);
          const status = detail.data?.status;
          if (status === "READY") {
            setProcessingStatus("Processing complete.");
            setOverlayProcessing(false);
            applyDateFields(detail.data || {});
            setGrossAmount(
              detail.data?.gross_amount != null
                ? String(detail.data.gross_amount)
                : ""
            );
            setNetAmount(
              detail.data?.net_amount != null
                ? String(detail.data.net_amount)
                : ""
            );
            setCurrency(detail.data?.currency || "EUR");
            return;
          }
          if (status === "FAILED") {
            setProcessingStatus(detail.data?.error_message || "Processing failed.");
            setOverlayProcessing(false);
            return;
          }
          if (attempt < 15) {
            setTimeout(() => poll(attempt + 1), 2000);
          } else {
            setProcessingStatus("Still processing. Check back later.");
            setOverlayProcessing(false);
          }
        } catch (err) {
          console.error("Status poll error:", err);
          setProcessingStatus("Error polling status.");
          setOverlayProcessing(false);
        }
      };
      poll();

      // keep values so auto-extracted metadata can populate the form
      setFile(null);
    } catch (err) {
      console.error("Upload error:", err);
      setMessage(
        err.response?.data?.error || "Failed to upload contract. Check console."
      );
      setOverlayProcessing(false);
    } finally {
      setLoading(false);
    }
  };

  const canUpload = hasPermission(user, "contracts:create");
  if (!canUpload) {
    const message = "You do not have permission to upload contracts.";
    return (
      <div className="max-w-3xl mx-auto">
        <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3">
          {message}
        </div>
      </div>
    );
  }

  const handleAddTypeQuick = async () => {
    const name = window.prompt("Enter new contract type name:");
    if (!name) return;
    try {
      await api.post("/contract-types", { name });
      await fetchTypes();
    } catch (err) {
      console.error("Error creating contract type:", err);
      alert("Failed to create type");
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-navy">Upload Contract</h1>
        <p className="text-sm text-slate-600 mt-1">
          Add a new contract, including legal duration and financial details,
          then attach the PDF document.
        </p>
      </div>

      {message && (
        <div className="mb-4 rounded-lg bg-amber-50 border border-amber-300 px-4 py-2 text-sm text-amber-900">
          {message}
        </div>
      )}
      {processingStatus && (
      <div className="mb-4 rounded-lg bg-slate-50 border border-slate-200 px-4 py-2 text-sm text-slate-800">
        {processingStatus}
      </div>
    )}
      {overlayProcessing && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 w-full max-w-sm text-center space-y-3">
            <div className="text-2xl animate-spin">⏳</div>
            <div className="text-sm font-semibold text-navy">
              Processing document…
            </div>
            <div className="text-xs text-slate-600">
              We’re extracting key fields (dates, duration, amounts). This usually takes a few seconds.
            </div>
          </div>
        </div>
      )}

      {/* Form card */}
      <form
        onSubmit={handleSubmit}
        className="bg-white shadow-sm rounded-xl border border-slate-200 p-6 space-y-5"
      >
        {/* Handwriting mode toggle */}
        <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200">
          <input
            id="handwritingMode"
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-navy focus:ring-navy"
            checked={handwritingMode}
            onChange={(e) => setHandwritingMode(e.target.checked)}
          />
          <label htmlFor="handwritingMode" className="text-sm text-slate-700 select-none">
            Handwriting mode (use vision OCR for all pages)
          </label>
        </div>

        {/* File upload first */}
        <div>
          <label className="block text-sm font-medium text-slate-800 mb-1">
            Contract PDF <span className="text-red-500">*</span>
          </label>

          <label className="flex flex-col items-center justify-center w-full border-2 border-dashed border-slate-300 rounded-xl px-4 py-6 text-center text-sm text-slate-500 hover:border-gold/80 hover:bg-amber-50 cursor-pointer transition">
            <span className="mb-1 text-2xl">📄</span>
            <span className="font-medium">
              {file ? file.name : "Drop contract PDF here or click to browse"}
            </span>
            <span className="text-xs mt-1">
              Supported format: PDF.
            </span>
            <input
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              className="hidden"
            />
          </label>
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-slate-800 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-slate-800 mb-1">
            Description
          </label>
          <textarea
            rows={2}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short summary (auto-filled when possible)"
          />
          <p className="text-xs text-slate-500 mt-1">
            Brief one-line description will auto-fill when available; you can edit.
          </p>
        </div>

        {/* Handwriting mode toggle */}
        {/* <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200">
          <input
            id="handwritingMode"
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-navy focus:ring-navy"
            checked={handwritingMode}
            onChange={(e) => setHandwritingMode(e.target.checked)}
          />
          <label htmlFor="handwritingMode" className="text-sm text-slate-700 select-none">
            Handwriting mode (use vision OCR for all pages, one-time higher token cost per file)
          </label>
        </div> */}

        {/* Type of contract + new type */}
        <div className="flex flex-col md:flex-row md:items-start gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-800 mb-1">
              Type of Contract
            </label>
            <div className="flex flex-col gap-1">
              <select
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
                value={selectedTypeId}
                onChange={(e) => setSelectedTypeId(e.target.value)}
              >
                <option value="">-- Select --</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              {previewTypeName && (
                <p className="text-xs text-slate-500">
                  Suggested: {previewTypeName} (choose or create manually)
                </p>
              )}
            </div>
          </div>
          <div className="md:mt-6">
            <button
              type="button"
              onClick={handleAddTypeQuick}
              className="inline-flex items-center justify-center px-3 py-2 rounded-lg border border-navy text-navy text-xs font-semibold hover:bg-navy hover:text-white"
            >
              + New Type
            </button>
          </div>
        </div>

        {/* Dates & duration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-800 mb-1">
              Start Date
            </label>
            <input
              type="date"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-800 mb-1">
              End Date
            </label>
            <input
              type="date"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70 disabled:bg-slate-100"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={isOpenEnded}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            id="openEnded"
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-navy focus:ring-navy"
            checked={isOpenEnded}
            onChange={(e) => setIsOpenEnded(e.target.checked)}
          />
          <label
            htmlFor="openEnded"
            className="text-sm text-slate-700 select-none"
          >
            Open-ended (no fixed end date)
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-800 mb-1">
            Duration (months)
          </label>
          <input
            type="number"
            min="0"
            className="w-full md:w-40 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
            value={durationMonths}
            onChange={(e) => setDurationMonths(e.target.value)}
          />
        </div>

        {/* Financial section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-800 mb-1">
              Gross Amount
            </label>
            <input
              type="number"
              step="0.01"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
              value={grossAmount}
              onChange={(e) => setGrossAmount(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-800 mb-1">
              Net Amount
            </label>
            <input
              type="number"
              step="0.01"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
              value={netAmount}
              onChange={(e) => setNetAmount(e.target.value)}
            />
          </div>
          {/* <div>
            <label className="block text-sm font-medium text-slate-800 mb-1">
              Currency
            </label>
            <input
              type="text"
              maxLength={3}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            />
          </div> */}
        </div>

        {/* Submit */}
        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-5 py-2.5 rounded-lg bg-navy text-white text-sm font-semibold shadow-sm hover:bg-navy/90 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "Uploading..." : "Upload Contract"}
          </button>
        </div>
      </form>
    </div>
  );
}
